import { NGXLogger } from 'ngx-logger';
import { Injectable } from '@angular/core';

import { CommsService, ICommsResponse } from '@services/comms/comms.service';
import { Events } from '@services/events/events.service';
import { BaseService } from './../abstract-base-service/abstract-base-service.service';
import { FoldersDataService } from '@services/folders/folders-data.service';
import { AssetsService } from '@services/assets/assets.service';
import { Permission, PermissionsService } from '@services/permissions/permissions.service';
import { SubscriberService } from '@services/subscriber/subscriber.service';
import { UserService } from '@services/user/user.service';
import { UserdataService } from '@services/userdata/userdata.service';
import { FolderDataType } from '@modules/management/modules/folders/model/folders.interfaces';

import { find, filter, get, groupBy, includes, map, some, sortBy } from 'lodash';
import {
  CalendarPreviewResult,
  FolderLocationInfo,
  MultiLocationScheduleConfig,
  ScheduleSelection,
  WorkSchedule,
  WorkScheduleEntry,
  WorkScheduleType
} from './model';

type WorkScheduleFilterCriteria = Pick<WorkSchedule, 'locationID'> & Partial<Pick<WorkSchedule, 'type'>>;

interface WorkScheduleCommandPayload {
  [key: string]: unknown;
}

type WorkScheduleUpsertPayload = WorkScheduleCommandPayload & Partial<Pick<WorkSchedule, 'name' | 'type' | 'locationID' | 'parentScheduleID' | 'scheduleID'>>;

type WorkScheduleEntryPayload = WorkScheduleCommandPayload & Omit<Partial<WorkScheduleEntry>, 'daysOfWeek'> & {
  daysOfWeek?: string | number[] | null;
};

type WorkerDayOff = Record<string, unknown>;

type WorkTimeCalculationResult = Record<string, unknown>;

@Injectable({
  providedIn: 'root'
})
export class WorkScheduleService extends BaseService {

  private workSchedules = {
    data: [] as WorkSchedule[],
    lastRequest: null,
    lastHash: null,
    lastFilterType: null as WorkScheduleType | null
  };

  constructor(
    private logger: NGXLogger,
    protected commsService: CommsService,
    private events: Events,
    private foldersDataService: FoldersDataService,
    private assetsService: AssetsService,
    private permissionsService: PermissionsService,
    private subscriberService: SubscriberService,
    private userService: UserService,
    private userData: UserdataService
  ) {
    super(commsService);
  }

  /**
   * Required by BaseService - refreshes work schedules cache
   */
  public refresh(): Promise<WorkSchedule[]> {
    return this.refreshWorkSchedules();
  }

  // ========== Work Schedules (Subscriber-level) ==========

  /**
   * Refresh work schedules cache
   * @param type Optional filter by WorkScheduleType
   * @param locationID Optional filter by locationID (0 = subscriber-level, >0 = location-specific)
   * @param updated Optional timestamp to check if cache is stale
   */
  public refreshWorkSchedules(type?: WorkScheduleType, locationID?: number, updated: Number = 0): Promise<WorkSchedule[]> {
    if (updated && updated < this.workSchedules.lastRequest) {
      this.logger.log(`local work schedules cache already up to date: ${updated}, ${this.workSchedules.lastRequest}`);
      return Promise.resolve(this.workSchedules.data);
    } else {
      return new Promise((resolve, reject) => {
        const when = Date.now();
        const params: Record<string, unknown> = {
          cmd: 'getWorkSchedules',
          includeEntries: 1,
          lastHash: this.workSchedules.lastHash,
          lastRequest: this.workSchedules.lastRequest,
          sendTime: when
        };
        if (type) {
          params.type = type;
        }
        if (locationID !== undefined) {
          params.locationID = locationID;
        }
        this.commsService.sendMessage(params, false, false).then(data => {
          if (data && data.reqStatus === 'OK') {
            this.workSchedules.lastHash = data.result.datahash;
            this.updateWorkSchedulesCache(data);
            this.workSchedules.lastFilterType = type || null;
          }
          resolve(this.workSchedules.data);
        }).catch((err) => {
          reject(err);
        });
      });
    }
  }

  public clearWorkSchedulesCache() {
    this.workSchedules.lastHash = null;
    this.workSchedules.lastRequest = null;
    this.workSchedules.data = null;
    this.workSchedules.lastFilterType = null;
  }

  /**
   * Get work schedules from cache, or fetch if cache is empty
   * Use this for initial page load to avoid unnecessary server calls
   */
  public async getWorkSchedules(): Promise<WorkSchedule[]> {
    if (!this.workSchedules.data || Object.keys(this.workSchedules.data).length === 0 || this.workSchedules.lastFilterType !== null) {
      await this.refreshWorkSchedules();
    }
    return this.workSchedules.data;
  }

  /**
   * Get cached work schedules synchronousdayOfWeekly (does not trigger refresh)
   * Use only when you know schedules have already been loaded
   */
  public getWorkSchedulesSync(): WorkSchedule[] {
    return this.workSchedules.data;
  }

  public updateWorkSchedulesCache(data): void {
    this.workSchedules.lastRequest = data.result.timestamp;
    this.workSchedules.data = data.result.schedules;
    this.events.publish('ccs:workSchedulesUpdate');
  }

  /**
   * Update cache from fetchAll data format
   * fetchAll returns workSchedules in data.result.workSchedules
   */
  public updateCache(data): void {
    this.workSchedules.lastRequest = data.result.timestamp;
    this.workSchedules.data = data.result.workSchedules;
    this.workSchedules.lastFilterType = null;
    this.events.publish('ccs:workSchedulesUpdate');
  }

  public getWorkScheduleByID(scheduleID: number): WorkSchedule {
    return find(this.workSchedules.data, { scheduleID: +scheduleID });
  }

  public canEdit(locationID: number | string, schedule?: WorkSchedule): boolean {
    locationID = +locationID;

    if (!this.hasLocationEditAccess(locationID)) {
      return false;
    }

    // If no schedule provided or no access attribute set, allow edit (default open)
    if (!schedule?.attributes?.access) {
      return true;
    }

    // Private schedules can only be edited by their creator
    if (schedule.attributes.access === 'private') {
      return schedule.creator === +this.userData.userID;
    }

    // Shared schedules are editable by anyone with location-level access
    return true;
  }

  private hasLocationEditAccess(locationID: number): boolean {
    const isCorvexUser = this.permissionsService.canView(Permission.Corvex);

    // Subscriber-level (locationID=0): fall back to subscriber-level settings
    if (!locationID) {
      const subscriberConfig = this.getSubscriberWorkScheduleAccessConfig();
      return some(subscriberConfig.permissions, (permission) => this.permissionsService.canView(permission)) || isCorvexUser;
    }

    const accessConfig = this.getWorkScheduleAccessConfig(locationID);

    return some(accessConfig.permissions, (permission) => this.permissionsService.canView(permission)) || isCorvexUser;
  }

  private getWorkScheduleAccessConfig(locationID: number): { permissions: string[] } {
    const location = this.userService.getLocation(locationID);
    const subscriberScheduleConfig = this.getSubscriberWorkScheduleAccessConfig();

    return {
      permissions: get(location, 'features.work_schedule_permission_access', subscriberScheduleConfig?.permissions || [])
    };
  }

  private getSubscriberWorkScheduleAccessConfig(): { permissions: string[] } {
    return {
      permissions: get(this.subscriberService.subInfo, 'features.work_schedule_permission_access', [])
    };
  }

  /**
   * Get work schedules filtered by type
   * @param type WorkScheduleType
   * @returns Array of schedules of the specified type
   */
  public getWorkSchedulesByType(type: WorkScheduleType): WorkSchedule[] {
    if (!this.workSchedules.data) {
      return [];
    }
    return filter(this.workSchedules.data, { type: type });
  }

  /**
   * Get work schedules for a specific location
   * @param locationID Location ID (0 = subscriber-level, >0 = location-specific)
   * @param type Optional filter by WorkScheduleType
   * @returns Array of schedules for the location
   */
  public getWorkSchedulesByLocation(locationID: number, type?: WorkScheduleType): WorkSchedule[] {
    if (!this.workSchedules.data) {
      return [];
    }
    const criteria: WorkScheduleFilterCriteria = { locationID };
    if (type) {
      criteria.type = type;
    }
    return filter<WorkSchedule>(this.workSchedules.data, criteria);
  }

  public handleAddWorkSchedule(fData: WorkScheduleUpsertPayload): Promise<ICommsResponse> {
    fData.cmd = 'addWorkSchedule';
    fData.sendTime = Date.now();
    return this.handleRequest(fData);
  }

  public handleUpdateWorkSchedule(fData: WorkScheduleUpsertPayload): Promise<ICommsResponse> {
    fData.cmd = 'updateWorkSchedule';
    fData.sendTime = Date.now();
    return this.handleRequest(fData);
  }

  public handleDeleteWorkSchedule(fData: WorkScheduleCommandPayload & { scheduleID: number }): Promise<ICommsResponse> {
    fData.cmd = 'deleteWorkSchedule';
    fData.sendTime = Date.now();
    return this.handleRequest(fData);
  }

  public handleSetDefaultSchedule(scheduleID: number): Promise<ICommsResponse> {
    return this.handleRequest({
      cmd: 'setDefaultWorkSchedule',
      scheduleID: scheduleID,
      sendTime: Date.now()
    });
  }

  // ========== Schedule Entries ==========

  public handleAddEntry(fData: WorkScheduleEntryPayload): Promise<ICommsResponse> {
    fData.cmd = 'addWorkScheduleEntry';
    fData.sendTime = Date.now();
    return this.handleRequest(fData);
  }

  public handleUpdateEntry(fData: WorkScheduleEntryPayload): Promise<ICommsResponse> {
    fData.cmd = 'updateWorkScheduleEntry';
    fData.sendTime = Date.now();
    return this.handleRequest(fData);
  }

  public handleDeleteEntry(entryID: number, scheduleID?: number): Promise<ICommsResponse> {
    const params: Record<string, unknown> = {
      cmd: 'deleteWorkScheduleEntry',
      entryID: entryID,
      sendTime: Date.now()
    };
    // Pass scheduleID to validate ownership (prevents deleting inherited entries)
    if (scheduleID) {
      params.scheduleID = scheduleID;
    }
    return this.handleRequest(params);
  }

  // ========== Subject Entries (Folder/Asset specific) ==========

  /**
   * Get entries attached directly to a subject (folder, asset, team, worker)
   * @param subjectType 'folder', 'asset', 'team', or 'worker'
   * @param subjectID The ID of the subject
   * @param scheduleType Optional filter by WorkScheduleType
   * @returns Promise with entries array
   */
  public getSubjectEntries(subjectType: string, subjectID: number, scheduleType?: WorkScheduleType): Promise<WorkScheduleEntry[]> {
    return new Promise((resolve, reject) => {
      const params: Record<string, unknown> = {
        cmd: 'getSubjectEntries',
        subjectType: subjectType,
        subjectID: subjectID,
        sendTime: Date.now()
      };
      if (scheduleType) {
        params.scheduleType = scheduleType;
      }
      this.commsService.sendMessage(params, false, false).then(data => {
        if (data && data.reqStatus === 'OK') {
          resolve(data.result.entries || []);
        } else {
          reject(data);
        }
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * Add an entry attached to a specific subject (folder, asset)
   * @param fData Entry data with subjectType and subjectID
   */
  public handleAddSubjectEntry(fData: WorkScheduleEntryPayload): Promise<ICommsResponse> {
    fData.cmd = 'addWorkScheduleEntry';
    fData.sendTime = Date.now();
    return this.handleRequest(fData);
  }

  /**
   * Update an entry attached to a specific subject
   */
  public handleUpdateSubjectEntry(fData: WorkScheduleEntryPayload): Promise<ICommsResponse> {
    fData.cmd = 'updateWorkScheduleEntry';
    fData.sendTime = Date.now();
    return this.handleRequest(fData);
  }

  /**
   * Delete a subject entry
   */
  public handleDeleteSubjectEntry(entryID: number): Promise<ICommsResponse> {
    return this.handleRequest({
      cmd: 'deleteWorkScheduleEntry',
      entryID: entryID,
      sendTime: Date.now()
    });
  }

  public handleExcludeEntry(scheduleID: number, entryID: number): Promise<ICommsResponse> {
    return this.handleRequest({
      cmd: 'excludeWorkScheduleEntry',
      scheduleID: scheduleID,
      entryID: entryID,
      sendTime: Date.now()
    });
  }

  public handleIncludeEntry(scheduleID: number, entryID: number): Promise<ICommsResponse> {
    return this.handleRequest({
      cmd: 'includeWorkScheduleEntry',
      scheduleID: scheduleID,
      entryID: entryID,
      sendTime: Date.now()
    });
  }

  // ========== Worker Days Off ==========

  public getWorkerDaysOff(pid: number, startDate?: number, endDate?: number): Promise<WorkerDayOff[]> {
    return new Promise((resolve, reject) => {
      const params: Record<string, unknown> = {
        cmd: 'getWorkerDaysOff',
        pid: pid,
        sendTime: Date.now()
      };
      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }
      this.commsService.sendMessage(params, false, false).then(data => {
        if (data && data.reqStatus === 'OK') {
          resolve(data.result.daysOff);
        } else {
          reject(data);
        }
      }).catch((err) => {
        reject(err);
      });
    });
  }

  public handleAddWorkerDayOff(fData: WorkScheduleCommandPayload): Promise<ICommsResponse> {
    fData.cmd = 'addWorkerDayOff';
    fData.sendTime = Date.now();
    return this.handleRequest(fData);
  }

  public handleDeleteWorkerDayOff(workerDaysOffID: number): Promise<ICommsResponse> {
    return this.handleRequest({
      cmd: 'deleteWorkerDayOff',
      workerDaysOffID: workerDaysOffID,
      sendTime: Date.now()
    });
  }

  // ========== Work Time Calculations ==========

  public calculateWorkTime(locationID: number, startTime: number, endTime: number): Promise<WorkTimeCalculationResult> {
    return new Promise((resolve, reject) => {
      this.commsService.sendMessage({
        cmd: 'calculateWorkTime',
        locationID: locationID,
        startTime: startTime,
        endTime: endTime,
        sendTime: Date.now()
      }, false, false).then(data => {
        if (data && data.reqStatus === 'OK') {
          resolve(data.result);
        } else {
          reject(data);
        }
      }).catch((err) => {
        reject(err);
      });
    });
  }

  // ========== Calendar Preview ==========

  /**
   * Get calendar preview showing working days and days off for a date range
   * @param workDaysScheduleID Schedule ID for work days (0 or -1 for none)
   * @param daysOffScheduleID Schedule ID for days off (0 or -1 for none)
   * @param startDate Start date in YYYY-MM-DD format
   * @param endDate End date in YYYY-MM-DD format
   * @param subjectType Optional subject type (e.g., 'assetFolder', 'asset') for subject-specific entries
   * @param subjectID Optional subject ID to include subject-specific entries
   * @returns Promise with workingDays, daysOff arrays and dayDetails object
   */
  public getCalendarPreview(
    workDaysScheduleID: number,
    daysOffScheduleID: number,
    startDate: string,
    endDate: string,
    subjectType?: string,
    subjectID?: number
  ): Promise<CalendarPreviewResult> {
    return new Promise((resolve, reject) => {
      const params: Record<string, unknown> = {
        cmd: 'getCalendarPreview',
        workDaysScheduleID: workDaysScheduleID > 0 ? workDaysScheduleID : 0,
        daysOffScheduleID: daysOffScheduleID > 0 ? daysOffScheduleID : 0,
        startDate: startDate,
        endDate: endDate,
        sendTime: Date.now()
      };

      // Add subject info if provided
      if (subjectType && subjectID && subjectID > 0) {
        params.subjectType = subjectType;
        params.subjectID = subjectID;
      }

      this.commsService.sendMessage(params, false, false).then(data => {
        if (data && data.reqStatus === 'OK') {
          resolve({
            workingDays: data.result.workingDays || [],
            daysOff: data.result.daysOff || [],
            dayDetails: data.result.dayDetails || {}
          });
        } else {
          reject(data);
        }
      }).catch((err) => {
        reject(err);
      });
    });
  }

  // ========== Schedule Selection Persistence ==========

  /**
   * Save schedule selection for a folder
   * @param folderID The folder ID
   * @param workDaysScheduleID Work days schedule ID (-1 for inherit)
   * @param daysOffScheduleID Days off schedule ID (-1 for inherit)
   */
  public saveFolderScheduleSelection(
    folderID: number,
    workDaysScheduleID: number,
    daysOffScheduleID: number
  ): Promise<ICommsResponse> {
    return new Promise((resolve, reject) => {
      // Use the folder update mechanism via commsService
      // workSchedule goes inside attributes
      const attributes = {
        workSchedule: {
          workDaysScheduleID: workDaysScheduleID,
          daysOffScheduleID: daysOffScheduleID
        }
      };

      this.commsService.sendMessage({
        cmd: 'updateFolder',
        folderID: folderID,
        attributes: JSON.stringify(attributes),
        sendTime: Date.now()
      }, false, false).then(data => {
        if (data && data.reqStatus === 'OK') {
          resolve(data);
        } else {
          reject(data);
        }
      }).catch(err => reject(err));
    });
  }

  /**
   * Save schedule selection for an asset
   * @param assetID The asset ID
   * @param workDaysScheduleID Work days schedule ID (-1 for inherit)
   * @param daysOffScheduleID Days off schedule ID (-1 for inherit)
   */
  public saveAssetScheduleSelection(
    assetID: number,
    workDaysScheduleID: number,
    daysOffScheduleID: number
  ): Promise<ICommsResponse> {
    return new Promise((resolve, reject) => {
      // Build the assets JSON structure for the API
      const assetUpdate = {
        assetID: {
           externalFields: {
            workSchedule: {
              workDaysScheduleID: workDaysScheduleID,
              daysOffScheduleID: daysOffScheduleID
            }
          }
        }
      };

      this.commsService.sendMessage({
        cmd: 'updateAsset',
        assets: JSON.stringify(assetUpdate),
        sendTime: Date.now()
      }, false, false).then(data => {
        if (data && data.reqStatus === 'OK') {
          resolve(data);
        } else {
          reject(data);
        }
      }).catch(err => reject(err));
    });
  }

  // ========== Helper Methods ==========

  /**
   * Get the default schedule IDs for the subscriber
   * Looks up schedules with isDefault=1 from the cached schedules
   * @returns Object with workDaysScheduleID and daysOffScheduleID (0 if not found)
   */
  public getDefaultScheduleIDs(): { workDaysScheduleID: number; daysOffScheduleID: number } {
    let workDaysScheduleID = 0;
    let daysOffScheduleID = 0;

    // Use lodash filter which handles both arrays and objects
    const defaultWorkDays = filter(this.workSchedules.data, { type: WorkScheduleType.Work_Days, isDefault: 1 });
    const defaultDaysOff = filter(this.workSchedules.data, { type: WorkScheduleType.Days_Off, isDefault: 1 });

    if (defaultWorkDays.length > 0) {
      workDaysScheduleID = defaultWorkDays[0].scheduleID;
    }
    if (defaultDaysOff.length > 0) {
      daysOffScheduleID = defaultDaysOff[0].scheduleID;
    }

    return { workDaysScheduleID, daysOffScheduleID };
  }

  /**
   * Get default schedule IDs for a specific location
   * First looks for location-specific defaults, then falls back to subscriber-level defaults
   * @param locationID The location ID to get defaults for
   * @returns Object with schedule IDs and names (0/empty if not found)
   */
  public getLocationDefaultSchedules(locationID: number): {
    workDaysScheduleID: number;
    daysOffScheduleID: number;
    workDaysScheduleName: string;
    daysOffScheduleName: string;
  } {
    let workDaysScheduleID = 0;
    let daysOffScheduleID = 0;
    let workDaysScheduleName = '';
    let daysOffScheduleName = '';

    // FIRST: Check location's preferences for explicit default schedule assignments
    // This is where the UI saves "Default Work Days Schedule" and "Default Days Off Schedule"
    const location = this.userService.getLocation(locationID);
    if (location?.preferences?.workSchedule) {
      const locPrefs = location.preferences.workSchedule;

      if (locPrefs.defaultWorkDaysScheduleID && locPrefs.defaultWorkDaysScheduleID > 0) {
        workDaysScheduleID = locPrefs.defaultWorkDaysScheduleID;
        const schedule = this.getWorkScheduleByID(workDaysScheduleID);
        workDaysScheduleName = schedule?.name || '';
      }

      if (locPrefs.defaultDaysOffScheduleID && locPrefs.defaultDaysOffScheduleID > 0) {
        daysOffScheduleID = locPrefs.defaultDaysOffScheduleID;
        const schedule = this.getWorkScheduleByID(daysOffScheduleID);
        daysOffScheduleName = schedule?.name || '';
      }
    }

    // SECOND: If not set in preferences, look for location-specific schedules with isDefault=1
    if (!workDaysScheduleID) {
      const locationWorkDays = filter(this.workSchedules.data, (s) =>
        s.type === WorkScheduleType.Work_Days && s.isDefault && s.locationID === locationID
      );
      if (locationWorkDays.length > 0) {
        workDaysScheduleID = locationWorkDays[0].scheduleID;
        workDaysScheduleName = locationWorkDays[0].name;
      }
    }
    if (!daysOffScheduleID) {
      const locationDaysOff = filter(this.workSchedules.data, (s) =>
        s.type === WorkScheduleType.Days_Off && s.isDefault && s.locationID === locationID
      );
      if (locationDaysOff.length > 0) {
        daysOffScheduleID = locationDaysOff[0].scheduleID;
        daysOffScheduleName = locationDaysOff[0].name;
      }
    }

    // THIRD: Fall back to subscriber-level defaults (locationID = 0)
    if (!workDaysScheduleID) {
      const subscriberWorkDays = filter(this.workSchedules.data, (s) =>
        s.type === WorkScheduleType.Work_Days && s.isDefault && (s.locationID === 0 || !s.locationID)
      );
      if (subscriberWorkDays.length > 0) {
        workDaysScheduleID = subscriberWorkDays[0].scheduleID;
        workDaysScheduleName = subscriberWorkDays[0].name;
      }
    }
    if (!daysOffScheduleID) {
      const subscriberDaysOff = filter(this.workSchedules.data, (s) =>
        s.type === WorkScheduleType.Days_Off && s.isDefault && (s.locationID === 0 || !s.locationID)
      );
      if (subscriberDaysOff.length > 0) {
        daysOffScheduleID = subscriberDaysOff[0].scheduleID;
        daysOffScheduleName = subscriberDaysOff[0].name;
      }
    }

    return { workDaysScheduleID, daysOffScheduleID, workDaysScheduleName, daysOffScheduleName };
  }

  // ========== Multi-Location Schedule Support ==========

  /**
   * Get unique locations for a folder's assets using cached frontend data
   * @param folderID The folder ID to query
   * @returns Promise with array of locations (locationID, locationName, assetCount)
   */
  public getFolderLocations(folderID: number): Promise<FolderLocationInfo[]> {
    return new Promise((resolve) => {
      // Get all folders in this folder's hierarchy (including itself and descendants)
      const folderTree = this.foldersDataService.getFolderTree(FolderDataType.ASSET, folderID);
      const folderIDs = map(folderTree, 'folderID');

      // Get all active assets and filter to those in our folder hierarchy
      const allAssets = this.assetsService.getActiveAssets();
      const folderAssets = filter(allAssets, (asset) =>
        includes(folderIDs, asset.folderID) && asset.active !== 0
      );

      // Group assets by location ID (property is 'location' in assets)
      const assetsByLocation = groupBy(folderAssets, 'location');

      // Build location info array
      const locations: FolderLocationInfo[] = [];
      for (const locationID of Object.keys(assetsByLocation)) {
        const lid = parseInt(locationID, 10);
        const assets = assetsByLocation[locationID];
        const location = this.userService.getLocationByID(lid);

        locations.push({
          locationID: lid,
          locationName: location?.name || '',
          assetCount: assets.length
        });
      }

      // Sort by location name
      const sortedLocations = sortBy(locations, (location) => (location.locationName || '').toLowerCase());

      resolve(sortedLocations);
    });
  }

  /**
   * Save per-location schedule configuration for a folder
   * @param folderID The folder ID
   * @param globalSchedule Global/default schedule settings
   * @param locationSchedules Map of locationID -> schedule settings
   */
  public saveFolderMultiLocationSchedule(
    folderID: number,
    globalSchedule: ScheduleSelection,
    locationSchedules: { [locationID: string]: ScheduleSelection }
  ): Promise<ICommsResponse> {
    return new Promise((resolve, reject) => {
      const workSchedule: MultiLocationScheduleConfig = {
        mode: 'per-location',
        global: globalSchedule,
        locations: locationSchedules
      };

      // workSchedule goes inside attributes
      const attributes = {
        workSchedule: workSchedule
      };

      this.commsService.sendMessage({
        cmd: 'updateFolder',
        folderID: folderID,
        attributes: JSON.stringify(attributes),
        sendTime: Date.now()
      }, false, false).then(data => {
        if (data && data.reqStatus === 'OK') {
          resolve(data);
        } else {
          reject(data);
        }
      }).catch(err => reject(err));
    });
  }

  /**
   * Save team multi-location schedule configuration (per-location mode)
   */
  public saveTeamMultiLocationSchedule(
    groupID: number,
    globalSchedule: ScheduleSelection,
    locationSchedules: { [locationID: string]: ScheduleSelection }
  ): Promise<ICommsResponse> {
    return new Promise((resolve, reject) => {
      const workSchedule: MultiLocationScheduleConfig = {
        mode: 'per-location',
        global: globalSchedule,
        locations: locationSchedules
      };

      // workSchedule goes inside attributes
      const attributes = {
        workSchedule: workSchedule
      };

      this.commsService.sendMessage({
        cmd: 'updateGroup',
        groupID: groupID,
        attributes: JSON.stringify(attributes),
        sendTime: Date.now()
      }, false, false).then(data => {
        if (data && data.reqStatus === 'OK') {
          resolve(data);
        } else {
          reject(data);
        }
      }).catch(err => {
        this.logger.error('[WorkScheduleService] saveTeamMultiLocationSchedule - error:', err);
        reject(err);
      });
    });
  }

  /**
   * Save team single schedule selection
   */
  public saveTeamScheduleSelection(
    groupID: number,
    workDaysScheduleID: number,
    daysOffScheduleID: number
  ): Promise<ICommsResponse> {
    return new Promise((resolve, reject) => {
      const attributes = {
        workSchedule: {
          workDaysScheduleID: workDaysScheduleID,
          daysOffScheduleID: daysOffScheduleID
        }
      };

      this.commsService.sendMessage({
        cmd: 'updateGroup',
        groupID: groupID,
        attributes: JSON.stringify(attributes),
        sendTime: Date.now()
      }, false, false).then(data => {
        if (data && data.reqStatus === 'OK') {
          resolve(data);
        } else {
          reject(data);
        }
      }).catch(err => {
        this.logger.error('[WorkScheduleService] saveTeamScheduleSelection - error:', err);
        reject(err);
      });
    });
  }

  /**
   * Get calendar preview with optional location filter for per-location schedules
   * @param workDaysScheduleID Schedule ID (-1 for location default)
   * @param daysOffScheduleID Schedule ID (-1 for location default)
   * @param startDate Start date YYYY-MM-DD
   * @param endDate End date YYYY-MM-DD
   * @param locationID Optional location ID for location-specific preview
   * @param subjectType Optional subject type
   * @param subjectID Optional subject ID
   */
  public getCalendarPreviewForLocation(
    workDaysScheduleID: number,
    daysOffScheduleID: number,
    startDate: string,
    endDate: string,
    locationID: number,
    subjectType?: string,
    subjectID?: number
  ): Promise<CalendarPreviewResult> {
    return new Promise((resolve, reject) => {
      const params: Record<string, unknown> = {
        cmd: 'getCalendarPreview',
        workDaysScheduleID: workDaysScheduleID,
        daysOffScheduleID: daysOffScheduleID,
        startDate: startDate,
        endDate: endDate,
        locationID: locationID,
        sendTime: Date.now()
      };

      if (subjectType && subjectID && subjectID > 0) {
        params.subjectType = subjectType;
        params.subjectID = subjectID;
      }

      this.commsService.sendMessage(params, false, false).then(data => {
        if (data && data.reqStatus === 'OK') {
          resolve({
            workingDays: data.result.workingDays || [],
            daysOff: data.result.daysOff || [],
            dayDetails: data.result.dayDetails || {}
          });
        } else {
          reject(data);
        }
      }).catch(err => reject(err));
    });
  }
}
